import { Prisma } from "encamp-prisma/generated/browser";

export type WillReportInput = {
  isAlwaysReported?: boolean | null;
  isExempt?: boolean | null;
  isOverThreshold?: boolean | null;
  chemical?: { noHazardsNotReporting?: boolean | null } | null;
};

/**
 * THE reporting-determination predicate. The stored
 * ReportingFacilityChemical.willReport column is a cache of this function,
 * written by aggregation; readers use `rfc.willReport ??
 * computeWillReport(rfc)` so a missing cache recomputes with the same logic.
 *
 * Intentional hierarchy:
 * - noHazardsNotReporting > the user explicitly excluded this product from
 *   Tier II reporting; beats everything, including voluntary reporting
 * - isAlwaysReported > the user explicitly volunteered this product
 * - isExempt > a statutory 40 CFR 370.13 exemption; suppresses
 *   threshold-driven reporting (but not voluntary reporting above)
 * - isOverThreshold > computed by aggregation for the specific
 *   municipality; null (malformed or not yet computed) defaults to
 *   reporting so validation surfaces the product instead of silently
 *   dropping it
 */
export function computeWillReport(rfc: WillReportInput): boolean {
  if (rfc.chemical?.noHazardsNotReporting) {
    return false;
  }
  if (rfc.isAlwaysReported) {
    return true;
  }
  if (rfc.isExempt) {
    return false;
  }
  return rfc.isOverThreshold ?? true;
}

/**
 * SQL mirror of `computeWillReport`, applied only to rows whose `willReport`
 * cache is still NULL. NULL is not always transient — `manuallyEdited` rows
 * and reporting years outside the aggregation window (`currentYear-4 … now`)
 * never get stamped — so a bare `willReport: null` reader would treat a
 * genuinely under-threshold row as reporting. This fragment recomputes from
 * the same inputs `computeWillReport` reads, so the SQL filters agree with the
 * grid cells' `rfc.willReport ?? computeWillReport(rfc)` on those rows.
 *
 * It is the one place this predicate is expressed in SQL; consumers compose
 * the exported where-clauses below rather than copying it.
 */
const reportingFromInputs = {
  AND: [
    // noHazardsNotReporting !== true (NULL-safe; excluded chemicals never report)
    {
      OR: [
        { chemical: { noHazardsNotReporting: false } },
        { chemical: { noHazardsNotReporting: null } },
      ],
    },
    {
      OR: [
        { isAlwaysReported: true },
        {
          AND: [
            { OR: [{ isExempt: false }, { isExempt: null }] },
            { OR: [{ isOverThreshold: true }, { isOverThreshold: null }] },
          ],
        },
      ],
    },
  ],
} satisfies Prisma.ReportingFacilityChemicalWhereInput;

/**
 * The input-derived predicate's explicit complement, for still-NULL rows.
 * Hand-written rather than `{ NOT: reportingFromInputs }`: `isAlwaysReported`
 * is nullable, and Prisma's NOT emits plain SQL `NOT(... = TRUE ...)`, which
 * is NULL (not TRUE) when the column is NULL — so a NULL-flag row would match
 * NEITHER bucket. Stating every required atom positively means a NULL can only
 * cause a correct non-match. This mirrors computeWillReport's inverse exactly:
 * excluded, OR (not-voluntary AND (exempt OR explicitly-under-threshold)).
 */
const notReportingFromInputs = {
  OR: [
    { chemical: { noHazardsNotReporting: true } },
    {
      AND: [
        { OR: [{ isAlwaysReported: false }, { isAlwaysReported: null }] },
        { OR: [{ isExempt: true }, { isOverThreshold: false }] },
      ],
    },
  ],
} satisfies Prisma.ReportingFacilityChemicalWhereInput;

/**
 * Prisma where fragment for "this RFC reports": trust the stamped column, and
 * for still-NULL rows fall back to the input-derived predicate above.
 */
export const willReportReportingWhere = {
  OR: [{ willReport: true }, { AND: [{ willReport: null }, reportingFromInputs] }],
} satisfies Prisma.ReportingFacilityChemicalWhereInput;

/**
 * Prisma where fragment for "this RFC does not report": stamped false, or
 * still-NULL and not-reporting by the explicit inverse above.
 */
export const willReportNotReportingWhere = {
  OR: [{ willReport: false }, { AND: [{ willReport: null }, notReportingFromInputs] }],
} satisfies Prisma.ReportingFacilityChemicalWhereInput;
